import axios from 'axios';
import { displayOptionToSessionStorageKeyMap } from '@tempus/ehr-integration-shared';
import { parseRedirectUrl } from './url-utils';
import logger from './logger';
import envConfig from '../config/config';

const {
  apps: { ehrIntegrationService: ehrIntegrationServiceURL, providerAccount: providerAccountURL },
} = envConfig;

const ehrIntegrationService = axios.create({
  baseURL: ehrIntegrationServiceURL,
});
const providerAccountService = axios.create({
  baseURL: providerAccountURL,
});

const EHR_SESSION_KEYS = {
  LAUNCH_ISSUER: 'EHR_HUB_LAUNCH_ISSUER',
  CLIENT_ID: 'EHR_CLIENT_ID',
  PROVIDER_EMAIL: 'EHR_PROVIDER_EMAIL',
  /** Okta Identity Provider id (e.g. 00o…); sent as authorize `idp` to jump straight to that IdP SSO. */
  OKTA_IDP_ID: 'EHR_OKTA_IDP_ID',
};

export function isValidEHRQueryParams(qs) {
  const validLaunchParameters = !!qs?.iss && !!qs?.launch;
  const validCodeExchangeParameters = !!qs?.code;
  return validLaunchParameters || validCodeExchangeParameters;
}

/** True if this document is in a (possibly cross-origin) child frame. */
export function isEmbeddedInFrame() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Provider email from EHR code-exchange, used for IDP discovery / iframe SSO hint. */
export function getStoredEhrProviderEmail() {
  const raw = sessionStorage.getItem(EHR_SESSION_KEYS.PROVIDER_EMAIL);
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/** Okta IdP id from EHR code-exchange; used as authorize `idp` (not login_hint). */
export function getStoredEhrOktaIdpId() {
  const raw = sessionStorage.getItem(EHR_SESSION_KEYS.OKTA_IDP_ID);
  if (raw == null) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

function pickOktaIdpIdFromCodeExchangePayload(data) {
  const raw = data?.idpId;
  if (raw == null) {
    return null;
  }
  const trimmed = String(raw).trim();
  return trimmed === '' ? null : trimmed;
}

function setEhrDisplayOptions(displayOptions) {
  Object.keys(displayOptionToSessionStorageKeyMap).forEach((key) => {
    if (displayOptions[key]) {
      sessionStorage.setItem(displayOptionToSessionStorageKeyMap[key], 'true');
    }
  });
}

/**
 * Clear all EHR launch session state so a new launch (e.g. different doc/room on shared computer) doesn't use stale params.
 */
function clearEhrLaunchSession() {
  Object.values(EHR_SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
  Object.values(displayOptionToSessionStorageKeyMap).forEach((key) => sessionStorage.removeItem(key));
}

/**
 * Normalize redirect URL (decode if API sent it encoded) and return as-is.
 * Query params are expected to be part of redirectURL from ehr-integration-service code-exchange;
 * we pass them through without adding or stripping.
 */
function normalizeRedirectUrl(redirectURL) {
  const parsed = parseRedirectUrl(redirectURL);
  return parsed ? parsed.href : redirectURL;
}

export async function handleEhrLaunch(launch, iss) {
  try {
    clearEhrLaunchSession();
    sessionStorage.setItem(EHR_SESSION_KEYS.LAUNCH_ISSUER, iss);
    const resp = await ehrIntegrationService.post('/launch', {
      launch,
      iss,
    });
    logger.info(resp.data);
    if (resp?.data?.appMetaData?.clientId) {
      logger.info('Setting clientId in session storage', { clientId: resp.data.appMetaData.clientId });
      sessionStorage.setItem(EHR_SESSION_KEYS.CLIENT_ID, resp.data.appMetaData.clientId);
    }

    if (resp?.data?.redirectURL) {
      logger.info('Redirecting from EHR Launch to authorization page', { redirectURL: resp.data.redirectURL });
      window.location = resp.data.redirectURL;
    }
  } catch (error) {
    logger.error('Error while handling EHR Launch', { error: error.message });
  }
}

export async function handleEhrCodeExchange(code) {
  try {
    const iss = sessionStorage.getItem(EHR_SESSION_KEYS.LAUNCH_ISSUER);
    logger.debug('ISS from session storage', { iss: iss });

    const clientId = sessionStorage.getItem(EHR_SESSION_KEYS.CLIENT_ID);
    logger.debug('Client ID from session storage', { clientId: clientId });

    const resp = await ehrIntegrationService.post('/code-exchange', {
      code,
      issuer: iss,
      clientId,
    });

    // displayOptions: object with keys and values (e.g. { hideNavBar: true, hideTopSearch: false })
    const displayOptions = resp?.data?.displayOptions;
    if (displayOptions && typeof displayOptions === 'object' && !Array.isArray(displayOptions)) {
      logger.info('Setting display options in session storage', { displayOptions });
      setEhrDisplayOptions(displayOptions);
    } else if (resp?.data?.redirectURL) {
      logger.debug('Code-exchange did not return displayOptions; session storage EHR_* keys will not be set', {
        hasDisplayOptions: !!displayOptions,
        redirectURL: resp.data.redirectURL,
      });
    }

    if (resp?.data?.providerEmail) {
      logger.info('Handling post login actions for EHR launch', { email: resp.data.providerEmail });
      sessionStorage.setItem(EHR_SESSION_KEYS.PROVIDER_EMAIL, resp.data.providerEmail);

      // await PostLoginActions(resp.data.providerEmail);
    }

    // Optional: ehr-integration-service may omit idpId; never require it for a successful flow.
    const oktaIdpId = pickOktaIdpIdFromCodeExchangePayload(resp?.data);
    if (oktaIdpId) {
      logger.info('Storing Okta IdP id from EHR code-exchange for SSO routing', { oktaIdpId });
      sessionStorage.setItem(EHR_SESSION_KEYS.OKTA_IDP_ID, oktaIdpId);
    } else {
      sessionStorage.removeItem(EHR_SESSION_KEYS.OKTA_IDP_ID);
    }

    if (resp?.data?.redirectURL) {
      logger.info('Redirecting from code exchange to Hub page', { redirectURL: resp.data.redirectURL });
      sessionStorage.removeItem(EHR_SESSION_KEYS.LAUNCH_ISSUER);
      return normalizeRedirectUrl(resp.data.redirectURL);
    }

    return;
  } catch (error) {
    logger.error('Error while handling EHR code exchange', { error: error.message });
  }
}

export function triggerIDPDiscovery() {
  const providerEmail = sessionStorage.getItem(EHR_SESSION_KEYS.PROVIDER_EMAIL);
  sessionStorage.removeItem(EHR_SESSION_KEYS.PROVIDER_EMAIL);

  if (!providerEmail) {
    logger.error('Provider email not found in session storage');
    return;
  }

  const inputElement = document.getElementById('idp-discovery-username');

  if (!inputElement) {
    logger.debug('IDP discovery input element not found', { providerEmail });
    return;
  }

  inputElement.value = providerEmail;

  // Required for Okta widget to recognize the input change
  const event = new Event('input', { bubbles: true });
  inputElement.dispatchEvent(event);

  const submitButton = document.getElementById('idp-discovery-submit');
  submitButton.click();
}

async function PostLoginActions(email) {
  try {
    await providerAccountService.post(`/v1/ehr-launch/post-login/${encodeURIComponent(email)}`);
  } catch (error) {
    logger.warn('Error handling post login actions for EHR launch', { email, error: error.message });
  }
}

export const exportedForTesting = {
  ehrIntegrationService,
  providerAccountService,
  EHR_SESSION_KEYS,
  PostLoginActions,
};
